// The page side of the service worker (offline plan W1; the worker is
// sw/service-worker.js, built into dist/sw.js by scripts/build-sw.mjs; the
// emergency off switch is sw/sw-killswitch.js, docs/offline-service-worker.md).
//
// - registerServiceWorker(): registers /sw.js (production builds only, from
//   main.tsx). Tells the worker which build this tab runs when it boots
//   (CB_CLIENT_READY, persisted by the worker so a frozen tab still holds its
//   build's cache), answers its later "which build" questions, and tracks
//   whether a new build is waiting.
// - The update flag drives the small "Update ready" prompt
//   (src/components/shared/UpdateReadyPrompt.tsx). Nothing reloads on its
//   own: the new build activates when the user taps, or on the next cold start
//   when the browser finds no tab on the old one. A tab that did not tap keeps
//   running its build, served from that build's retained cache.
// - purgeUserCaches(): account transitions (useAuth) delete every Cache
//   Storage entry that could hold user-derived data, after the worker has
//   stopped writing for the departing account. A failure leaves a tombstone
//   that main.tsx retries before the next load renders anything.
//
// Nothing here touches a browser global at module scope (tests and the
// prerender import the prompt, which imports this).

// Cache names. The worker's own constants are in sw/service-worker.js; these
// are the ones the page needs to know to purge.
/** The pre-W1 worker's cache. It stored every non-API GET, Supabase REST included. */
export const LEGACY_CACHE = 'cmdr-bracket-v1'
/** The worker's bounded card image cache (public Scryfall images). */
export const CARD_IMAGE_CACHE_PREFIX = 'cb-card-images-'
/** Owner-pinned offline images (offline plan W6 / contract C5: cb-img-<owner>). */
export const OWNER_IMAGE_CACHE_PREFIX = 'cb-img-'
/** localStorage: a purge that did not verify, retried on the next load. */
export const PURGE_TOMBSTONE_KEY = 'cb-cache-purge-pending'
/** sessionStorage: the one reload the kill switch asks for already happened. */
const KILL_RELOAD_KEY = 'cb.swKilledReload'

let updateReady = false
const listeners = new Set<() => void>()
let registration: ServiceWorkerRegistration | null = null
let reloadRequested = false

function setUpdateReady(next: boolean): void {
  if (updateReady === next) return
  updateReady = next
  for (const l of listeners) l()
}

/** True once a new build is installed and waiting (or active in another tab). */
export function isUpdateReady(): boolean {
  return updateReady
}

export function subscribeUpdateReady(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The entry script of the build this tab runs, as the worker's precache keys
 * it ("/assets/index-<hash>.js"), or null.
 */
export function currentBuildEntry(doc: Document = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
  if (!el?.src) return null
  try {
    return new URL(el.src, doc.baseURI).pathname
  } catch {
    return null
  }
}

/** Post `message` with a reply port; resolves with the reply, or null on timeout. */
function ask<T>(worker: ServiceWorker, message: object, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    let done = false
    const channel = new MessageChannel()
    const finish = (v: T | null) => {
      if (done) return
      done = true
      clearTimeout(timer)
      channel.port1.close()
      resolve(v)
    }
    const timer = setTimeout(() => finish(null), timeoutMs)
    channel.port1.onmessage = (e: MessageEvent) => finish(e.data as T)
    try {
      worker.postMessage(message, [channel.port2])
    } catch {
      finish(null)
    }
  })
}

/**
 * Tell a worker which build this tab runs. Resolves true when that worker
 * serves this build, false when it is another build, null with no answer.
 */
async function announce(worker: ServiceWorker | null | undefined): Promise<boolean | null> {
  if (!worker) return null
  // eslint-disable-next-line i18next/no-literal-string -- a message type
  const reply = await ask<{ sameBuild?: boolean }>(worker, { type: 'CB_CLIENT_READY', entry: currentBuildEntry() }, 2000)
  return typeof reply?.sameBuild === 'boolean' ? reply.sameBuild : null
}

// A worker that is 'installed' and still waiting after this long is an update
// for the user to take. One that skipped waiting (the one-time takeover from
// the pre-W1 worker) is long past 'installed' by then and never prompts.
const INSTALLED_SETTLE_MS = 1000

function watchInstalling(reg: ServiceWorkerRegistration, sw: ServiceWorker | null): void {
  if (!sw) return
  sw.addEventListener('statechange', () => {
    // Without a controller it is the first install, which activates by itself.
    if (sw.state !== 'installed' || !navigator.serviceWorker.controller) return
    setTimeout(() => {
      if (sw.state === 'installed' && reg.waiting === sw) setUpdateReady(true)
    }, INSTALLED_SETTLE_MS)
  })
}

/** Ask a waiting worker to take over and reload THIS tab into the new build. */
export function applyUpdate(): void {
  const waiting = registration?.waiting
  if (waiting) {
    reloadRequested = true
    // eslint-disable-next-line i18next/no-literal-string -- a message type
    waiting.postMessage({ type: 'CB_SKIP_WAITING' })
    return
  }
  // Another tab already activated it: this tab only needs to reload.
  window.location.reload()
}

function reloadOnceForKill(): void {
  try {
    if (sessionStorage.getItem(KILL_RELOAD_KEY) === '1') return
    sessionStorage.setItem(KILL_RELOAD_KEY, '1')
  } catch {
    // No sessionStorage: still reload; the worker that asked is gone after this.
  }
  window.location.reload()
}

const UPDATE_CHECK_MS = 30 * 60 * 1000

/**
 * Register /sw.js. Production only (main.tsx); a no-op without service
 * worker support. Safe to call once per page load. With the kill switch built
 * in (VITE_SW_KILL=1) it registers nothing and only asks the browser to fetch
 * /sw.js again, which is then the kill switch.
 */
export function registerServiceWorker({ kill = __SW_KILL__ }: { kill?: boolean } = {}): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  const container = navigator.serviceWorker
  const hadController = !!container.controller

  container.addEventListener('message', (event: MessageEvent) => {
    const type = event.data?.type
    // The worker asks every window which build it runs before it deletes an
    // older build's cache. Reply with this tab's entry script.
    if (type === 'CB_SW_WHICH_BUILD' && event.ports?.[0]) {
      event.ports[0].postMessage({ entry: currentBuildEntry() })
    } else if (type === 'CB_SW_KILLED') {
      reloadOnceForKill()
    }
  })
  container.startMessages?.()

  if (kill) {
    container.getRegistration().then(reg => reg?.update()).catch(() => {})
    return
  }

  container.addEventListener('controllerchange', () => {
    if (reloadRequested) {
      window.location.reload()
      return
    }
    // The first install claiming this tab is not an update, nor is the
    // one-time takeover from the pre-W1 worker when this tab already runs the
    // new build. A new build activated from ANOTHER tab is: this tab keeps
    // running its build (its chunks stay cached) and offers the reload.
    // No answer is not "another build": the worker may still be activating.
    // Ask once more, and prompt only on an explicit "another build" (a
    // missing prompt costs nothing, this tab keeps running on its cache).
    void announce(container.controller)
      .then(sameBuild => (sameBuild === null ? announce(container.controller) : sameBuild))
      .then((sameBuild) => {
        if (hadController && sameBuild === false) setUpdateReady(true)
      })
  })

  let lastCheck = Date.now()
  const register = () => {
    // Default options on purpose: under the default updateViaCache
    // ('imports') the main script already bypasses the HTTP cache (the worker
    // imports nothing), and it is the mode the pre-W1 registration has, so
    // register() never has to change the registration's settings.
    // eslint-disable-next-line i18next/no-literal-string -- a URL path
    container.register('/sw.js').then((reg) => {
      registration = reg
      if (reg.waiting && container.controller) setUpdateReady(true)
      watchInstalling(reg, reg.installing)
      reg.addEventListener('updatefound', () => watchInstalling(reg, reg.installing))
      // A long-lived tab (the Android app stays open for days) still learns
      // about a deploy: re-check when it comes back to the foreground.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || Date.now() - lastCheck < UPDATE_CHECK_MS) return
        lastCheck = Date.now()
        reg.update().catch(() => {})
      })
      return container.ready
    }).then((reg) => announce(reg.active)).catch(() => {})
  }
  if (document.readyState === 'complete') register()
  // eslint-disable-next-line i18next/no-literal-string -- an event name
  else window.addEventListener('load', register, { once: true })
}

/** Cache names that can hold user-derived data, or data a departing user viewed. */
export function isUserDerivedCache(name: string): boolean {
  return name === LEGACY_CACHE ||
    name.startsWith(OWNER_IMAGE_CACHE_PREFIX) ||
    name.startsWith(CARD_IMAGE_CACHE_PREFIX)
}

export interface PurgeResult {
  /** Verified: no user-derived cache is left. */
  ok: boolean
  deleted: string[]
  remaining: string[]
}

function writeTombstone(): void {
  try { localStorage.setItem(PURGE_TOMBSTONE_KEY, String(Date.now())) } catch { /* nothing to persist into */ }
}

function clearTombstone(): void {
  try { localStorage.removeItem(PURGE_TOMBSTONE_KEY) } catch { /* nothing persisted */ }
}

interface PurgeDeps {
  storage?: CacheStorage
  container?: ServiceWorkerContainer | null
  workerTimeoutMs?: number
}

/**
 * Delete every Cache Storage entry a departing account could have left: the
 * legacy worker cache (it held Supabase REST responses), owner-pinned image
 * caches, and the card image cache (which cards were on screen is the user's
 * collection and decks, even though each image is public). The build
 * precache is the app itself and stays.
 *
 * First the worker: CB_PURGE_USER_CACHES bumps its image generation, so a
 * card image still in flight for the departing account is never stored, and
 * it deletes on its side once its pending writes settled. Then this page
 * deletes and VERIFIES. Anything left (a rejected or failed deletion) writes
 * a tombstone that runPendingCachePurge() retries on the next load. Never
 * throws.
 */
export async function purgeUserCaches(deps: PurgeDeps = {}): Promise<PurgeResult> {
  const storage = 'storage' in deps ? deps.storage : globalThis.caches
  const container = 'container' in deps
    ? deps.container
    : (typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : null)
  const worker = container?.controller
  // eslint-disable-next-line i18next/no-literal-string -- a message type
  if (worker) await ask(worker, { type: 'CB_PURGE_USER_CACHES', generation: Date.now() }, deps.workerTimeoutMs ?? 3000)
  if (!storage) {
    clearTombstone()
    return { ok: true, deleted: [], remaining: [] }
  }
  try {
    const names = (await storage.keys()).filter(isUserDerivedCache)
    await Promise.all(names.map(n => storage.delete(n).catch(() => false)))
    const remaining = (await storage.keys()).filter(isUserDerivedCache)
    const ok = remaining.length === 0
    if (ok) clearTombstone()
    else writeTombstone()
    return { ok, deleted: names.filter(n => !remaining.includes(n)), remaining }
  } catch {
    writeTombstone()
    return { ok: false, deleted: [], remaining: [] }
  }
}

/** A purge from an earlier page load did not verify. */
export function hasPendingCachePurge(): boolean {
  try {
    return localStorage.getItem(PURGE_TOMBSTONE_KEY) !== null
  } catch {
    return false
  }
}

/** Retry an unverified purge (main.tsx, before the app renders). Never throws. */
export async function runPendingCachePurge(deps: PurgeDeps = {}): Promise<PurgeResult | null> {
  if (!hasPendingCachePurge()) return null
  return purgeUserCaches(deps)
}

/** Test seam: reset module state between tests. */
export function __resetServiceWorkerStateForTests(): void {
  updateReady = false
  listeners.clear()
  registration = null
  reloadRequested = false
}
